<script>
  import { onMount } from 'svelte';
  import { get } from 'svelte/store';
  import Pagination from '$lib/Pagination.svelte';
  import FeatureInfo from '$lib/FeatureInfo.svelte';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import {
    faRotate,
    faSquarePlus,
    faPencil,
    faTrashCan,
    faCircleXmark,
  } from '@fortawesome/pro-light-svg-icons';
  import { selectedStudy, selectedSite, selectedParticipant } from '$stores/selectionStore';
  import Toolbar from '$lib/Toolbar.svelte';
  import Footer from '$lib/Footer.svelte';
  import StudyHeader from '$lib/StudyHeader.svelte';
  import StudyDetails from '$lib/StudyDetails.svelte';
  import ConfirmationModal from '$lib/ConfirmationModal.svelte';
  import { userRoles, userReadOnly } from '$stores/sessionStore';
  import { studyProperties } from '$stores/configStore';
  import { hasAccessUiAction } from '$utils/permissionUtils.js';

  const API_BASE_URL = '/api/proxy';

  const STATUS_OPTIONS = [
    { value: 'available', label: 'Available' },
    { value: 'assigned', label: 'Assigned' },
    { value: 'in_repair', label: 'In repair' },
    { value: 'retired', label: 'Retired' },
  ];

  function statusLabel(code) {
    const row = STATUS_OPTIONS.find((o) => o.value === code);
    return row ? row.label : code || '';
  }

  let sites = [];
  let siteFilterId = '';
  let items = [];
  let resultCount = 0;
  let currentPage = 1;
  let totalPages = 1;
  let limit = 20;
  let searchText = '';
  let sortField = 'inventoryId';
  let sortOrder = 'asc';
  let isLoading = false;
  let loadError = '';

  let showModal = false;
  let showDeleteModal = false;
  let modalMode = 'create';
  let selectedRow = null;

  let formInventoryId = '';
  let formStatus = 'available';
  let formSubjectId = '';
  let formImei = '';
  let formManufacturer = '';
  let formModel = '';
  let formNotes = '';
  let formError = '';

  onMount(() => {
    selectedParticipant.set(null);
  });

  $: studyKey = $selectedStudy?._id;

  $: if (studyKey) {
    loadSitesForStudy();
  } else {
    sites = [];
    siteFilterId = '';
    items = [];
    resultCount = 0;
  }

  async function loadSitesForStudy() {
    if (!$selectedStudy?._id) return;
    try {
      const res = await fetch(`/api/v1/sites/study/${$selectedStudy._id}`);
      if (!res.ok) throw new Error('Failed to load sites');
      const data = await res.json();
      sites = Array.isArray(data) ? data : data.data || [];
      if (!sites.some((s) => s._id === siteFilterId)) {
        const preferred =
          $selectedSite?._id && sites.some((s) => s._id === $selectedSite._id)
            ? $selectedSite._id
            : sites[0]?._id || '';
        siteFilterId = preferred;
      }
      if (siteFilterId) {
        await fetchItems(1);
      } else {
        items = [];
        resultCount = 0;
      }
    } catch (e) {
      console.error(e);
      sites = [];
      siteFilterId = '';
      items = [];
      resultCount = 0;
    }
  }

  async function fetchItems(page = 1) {
    if (!$selectedStudy?._id || !siteFilterId) return;
    try {
      isLoading = true;
      loadError = '';
      let url = `${API_BASE_URL}/phoneInventory?page=${page}&pageSize=${limit}&sortField=${sortField}&sortOrder=${sortOrder}&studyGUID=${$selectedStudy._id}&siteGUID=${siteFilterId}`;
      if (searchText) {
        url += `&searchText=${encodeURIComponent(searchText)}`;
      }
      const res = await fetch(url);
      const body = await res.json();
      if (!res.ok) {
        throw new Error(body.message || 'Failed to load inventory');
      }
      resultCount = body.totalCount || 0;
      currentPage = body.page || 1;
      totalPages = body.totalPages || 1;
      items = body.data || [];
    } catch (err) {
      console.error(err);
      loadError = err.message || 'Failed to load';
      items = [];
    } finally {
      isLoading = false;
    }
  }

  function handleRefresh() {
    fetchItems(currentPage);
  }

  function handlePageChange(newPage) {
    if (newPage > 0 && newPage <= totalPages) {
      fetchItems(newPage);
    }
  }

  function handleSort(field) {
    if (sortField === field) {
      sortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      sortField = field;
      sortOrder = 'asc';
    }
    fetchItems(1);
  }

  function handleClearSearch() {
    searchText = '';
    fetchItems(1);
  }

  function openCreate() {
    modalMode = 'create';
    formInventoryId = '';
    formStatus = 'available';
    formSubjectId = '';
    formImei = '';
    formManufacturer = '';
    formModel = '';
    formNotes = '';
    formError = '';
    showModal = true;
  }

  function openEdit(row) {
    modalMode = 'edit';
    selectedRow = row;
    formInventoryId = row.inventoryId || '';
    formStatus = row.status || 'available';
    formSubjectId = row.participantSubjectId || '';
    formImei = row.imei || '';
    formManufacturer = row.manufacturer || '';
    formModel = row.model || '';
    formNotes = row.notes || '';
    formError = '';
    showModal = true;
  }

  function openDelete(row) {
    selectedRow = row;
    showDeleteModal = true;
  }

  async function saveModal() {
    formError = '';
    if (!formInventoryId.trim()) {
      formError = 'Inventory ID is required.';
      return;
    }
    if (formStatus === 'assigned' && !formSubjectId.trim()) {
      formError = 'Enter participant subject id when status is Assigned.';
      return;
    }
    if (!$selectedStudy?._id || !siteFilterId) {
      formError = 'Study and site are required.';
      return;
    }

    if (!hasAccessUiAction('phoneInventory.edit', get(userRoles), get(studyProperties), get(userReadOnly)) && modalMode === 'edit') {
      formError = 'Insufficient permissions.';
      return;
    }
    if (!hasAccessUiAction('phoneInventory.add', get(userRoles), get(studyProperties), get(userReadOnly)) && modalMode === 'create') {
      formError = 'Insufficient permissions.';
      return;
    }

    const payload = {
      studyGUID: $selectedStudy._id,
      siteGUID: siteFilterId,
      inventoryId: formInventoryId.trim(),
      status: formStatus,
      subjectId: formStatus === 'assigned' ? formSubjectId.trim() : '',
      imei: formImei,
      manufacturer: formManufacturer,
      model: formModel,
      notes: formNotes,
    };

    try {
      isLoading = true;
      let res;
      if (modalMode === 'create') {
        res = await fetch(`${API_BASE_URL}/phoneInventory`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } else {
        res = await fetch(`${API_BASE_URL}/phoneInventory/${selectedRow._id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        formError = data.message || 'Save failed';
        return;
      }
      showModal = false;
      await fetchItems(modalMode === 'create' ? 1 : currentPage);
    } catch (e) {
      console.error(e);
      formError = 'Save failed';
    } finally {
      isLoading = false;
    }
  }

  async function confirmDelete() {
    if (!selectedRow?._id) return;
    if (!hasAccessUiAction('phoneInventory.delete', get(userRoles), get(studyProperties), get(userReadOnly))) {
      return;
    }
    try {
      isLoading = true;
      const res = await fetch(`${API_BASE_URL}/phoneInventory/${selectedRow._id}`, {
        method: 'DELETE',
      });
      if (!res.ok && res.status !== 204) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Delete failed');
      }
      showDeleteModal = false;
      await fetchItems(currentPage);
    } catch (e) {
      console.error(e);
    } finally {
      isLoading = false;
    }
  }

  $: canAdd = hasAccessUiAction('phoneInventory.add', $userRoles, $studyProperties, $userReadOnly);
  $: canEdit = hasAccessUiAction('phoneInventory.edit', $userRoles, $studyProperties, $userReadOnly);
  $: canDelete = hasAccessUiAction('phoneInventory.delete', $userRoles, $studyProperties, $userReadOnly);
</script>

<StudyHeader />

<FeatureInfo
  title="Participant phone inventory"
  description="Track study phones by inventory ID per site. Assign a phone to a participant using their subject ID when the status is Assigned."
  marginBottom="mb-0"
/>

<StudyDetails showOptionsMenu={false} />

<Toolbar title="Phone inventory" itemCountTitle="Phones" itemCount={resultCount}>
  <div class="flex flex-wrap items-center gap-3">
    <label class="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-100">
      <span>Site</span>
      <select
        class="px-2 py-1.5 text-xs rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
        bind:value={siteFilterId}
        on:change={() => siteFilterId && fetchItems(1)}
      >
        {#if sites.length === 0}
          <option value="">—</option>
        {:else}
          {#each sites as s}
            <option value={s._id}>{s.siteId} — {s.siteName}</option>
          {/each}
        {/if}
      </select>
    </label>
    <div class="relative">
      <input
        type="text"
        placeholder="Search"
        bind:value={searchText}
        on:input={() => fetchItems(1)}
        class="px-3 py-1.5 text-xs text-gray-800 dark:text-gray-100 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 pr-8"
      />
      {#if searchText}
        <button
          type="button"
          on:click={handleClearSearch}
          class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          title="Clear search"
        >
          <FontAwesomeIcon icon={faCircleXmark} />
        </button>
      {/if}
    </div>
    <button
      type="button"
      on:click={openCreate}
      class="pagination mx-1 p-0"
      disabled={!canAdd || !siteFilterId}
      class:opacity-50={!canAdd || !siteFilterId}
      title={!canAdd ? 'Insufficient permissions' : !siteFilterId ? 'Select a site' : 'Add phone'}
    >
      <FontAwesomeIcon icon={faSquarePlus} />
    </button>
    <button type="button" on:click={handleRefresh} class="pagination mx-1 p-0" disabled={isLoading}>
      <FontAwesomeIcon icon={faRotate} spin={isLoading} />
    </button>
  </div>
</Toolbar>

{#if !$selectedStudy?._id}
  <p class="px-4 py-6 text-sm text-gray-600 dark:text-gray-400">Select a study in the header to manage phone inventory.</p>
{:else if loadError}
  <p class="px-4 py-6 text-sm text-red-600">{loadError}</p>
{/if}

<div class="px-1 pt-0">
  <table class="min-w-full border-collapse table-auto">
    <thead>
      <tr class="text-sm text-black dark:text-slate-300 bg-stone-200/50 dark:bg-gray-800/90">
        <th class="px-4 py-2 text-left cursor-pointer" on:click={() => handleSort('inventoryId')}>
          Inventory ID {sortField === 'inventoryId' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
        </th>
        <th class="px-4 py-2 text-left cursor-pointer" on:click={() => handleSort('status')}>
          Status {sortField === 'status' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
        </th>
        <th class="px-4 py-2 text-left">Participant</th>
        <th class="px-4 py-2 text-left">IMEI</th>
        <th class="px-4 py-2 text-left">Manufacturer</th>
        <th class="px-4 py-2 text-left">Model</th>
        <th class="px-4 py-2 text-left">Notes</th>
        <th class="w-px px-4 py-2 text-left">Actions</th>
      </tr>
    </thead>
    <tbody>
      {#each items as row, index}
        <tr
          class="text-sm hover:bg-white dark:hover:bg-gray-775 text-gray-800 dark:text-slate-300 {index %
            2 ===
          0
            ? 'bg-stone-50 dark:bg-gray-725'
            : 'bg-stone-75 dark:bg-gray-750'}"
        >
          <td class="px-4 py-2 font-medium">{row.inventoryId}</td>
          <td class="px-4 py-2">{statusLabel(row.status)}</td>
          <td class="px-4 py-2">{row.participantSubjectId || '—'}</td>
          <td class="px-4 py-2">{row.imei || ''}</td>
          <td class="px-4 py-2">{row.manufacturer || ''}</td>
          <td class="px-4 py-2">{row.model || ''}</td>
          <td class="px-4 py-2 max-w-xs truncate" title={row.notes || ''}>{row.notes || ''}</td>
          <td class="w-px px-4 py-2">
            <div class="flex items-center space-x-2">
              <button
                type="button"
                on:click={() => openEdit(row)}
                class="pagination mx-1 p-0"
                disabled={!canEdit}
                class:opacity-50={!canEdit}
                title="Edit"
              >
                <FontAwesomeIcon icon={faPencil} class="text-sky-800 dark:text-white/70" />
              </button>
              <button
                type="button"
                on:click={() => openDelete(row)}
                class="pagination mx-1 p-0"
                disabled={!canDelete}
                class:opacity-50={!canDelete}
                title="Delete"
              >
                <FontAwesomeIcon icon={faTrashCan} class="text-sky-800 dark:text-white/70" />
              </button>
            </div>
          </td>
        </tr>
      {/each}
      {#if siteFilterId && items.length === 0 && !isLoading}
        <tr>
          <td colspan="8" class="text-center text-gray-500 dark:text-slate-400 py-10">
            No phones in inventory for this site
          </td>
        </tr>
      {/if}
    </tbody>
  </table>
</div>

<Footer>
  <Pagination {currentPage} {totalPages} onPageChange={handlePageChange} />
</Footer>

{#if showModal}
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
    <div
      class="w-full max-w-lg rounded-lg bg-white dark:bg-gray-800 shadow-xl border border-gray-200 dark:border-gray-600 p-6"
      role="dialog"
      aria-modal="true"
    >
      <h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
        {modalMode === 'create' ? 'Add phone' : 'Edit phone'}
      </h2>
      <div class="space-y-3 text-sm">
        <div>
          <label class="block text-gray-700 dark:text-gray-300 mb-1" for="inv-id">Inventory ID</label>
          <input
            id="inv-id"
            class="w-full px-3 py-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
            bind:value={formInventoryId}
          />
        </div>
        <div>
          <label class="block text-gray-700 dark:text-gray-300 mb-1" for="inv-status">Status</label>
          <select
            id="inv-status"
            class="w-full px-3 py-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
            bind:value={formStatus}
          >
            {#each STATUS_OPTIONS as o}
              <option value={o.value}>{o.label}</option>
            {/each}
          </select>
        </div>
        {#if formStatus === 'assigned'}
          <div>
            <label class="block text-gray-700 dark:text-gray-300 mb-1" for="inv-subj">Participant subject ID</label>
            <input
              id="inv-subj"
              class="w-full px-3 py-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
              bind:value={formSubjectId}
              placeholder="Subject ID at this site"
            />
          </div>
        {/if}
        <div>
          <label class="block text-gray-700 dark:text-gray-300 mb-1" for="inv-imei">IMEI</label>
          <input
            id="inv-imei"
            class="w-full px-3 py-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
            bind:value={formImei}
          />
        </div>
        <div class="grid grid-cols-2 gap-3">
          <div>
            <label class="block text-gray-700 dark:text-gray-300 mb-1" for="inv-mfg">Manufacturer</label>
            <input
              id="inv-mfg"
              class="w-full px-3 py-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
              bind:value={formManufacturer}
            />
          </div>
          <div>
            <label class="block text-gray-700 dark:text-gray-300 mb-1" for="inv-model">Model</label>
            <input
              id="inv-model"
              class="w-full px-3 py-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
              bind:value={formModel}
            />
          </div>
        </div>
        <div>
          <label class="block text-gray-700 dark:text-gray-300 mb-1" for="inv-notes">Notes</label>
          <textarea
            id="inv-notes"
            rows="2"
            class="w-full px-3 py-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
            bind:value={formNotes}
          />
        </div>
        {#if formError}
          <p class="text-red-600 text-sm">{formError}</p>
        {/if}
      </div>
      <div class="flex justify-end gap-2 mt-6">
        <button
          type="button"
          class="px-4 py-2 text-sm rounded border border-gray-300 dark:border-gray-600"
          on:click={() => (showModal = false)}
        >
          Cancel
        </button>
        <button
          type="button"
          class="px-4 py-2 text-sm rounded bg-sky-700 text-white hover:bg-sky-800"
          on:click={saveModal}
          disabled={isLoading}
        >
          Save
        </button>
      </div>
    </div>
  </div>
{/if}

{#if showDeleteModal && selectedRow}
  <ConfirmationModal
    showConfirmation={true}
    message={`Delete inventory record "${selectedRow.inventoryId}"? This does not change participant records.`}
    confirmPhrase="delete"
    on:confirm={confirmDelete}
    on:close={() => (showDeleteModal = false)}
  />
{/if}
